import { Column, ColumnDef, Row, RowData, TableOptions } from '@tanstack/react-table';
import { Dispatch, FunctionComponent, SetStateAction } from 'react';

import { StyledTableProps } from '../StyledTable/StyledTable.types';
import { TableRowProps } from '../StyledTable/TableRow/TableRow.types';
import { normalizeString } from '../utils';

declare module '@tanstack/table-core' {
  interface ColumnMeta<TData extends RowData, TValue> {
    disableDefaultFilter?: boolean;
    isDate?: boolean;
    columnSortingLabel?: string;
    customFilter?: FunctionComponent<{
      column: Column<TData, TValue>;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- We have to use `any` here
      filterValue: any;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- We have to use `any` here
      setFilterValue: Dispatch<SetStateAction<any | undefined>>;
    }>;
  }
}

export interface DataTableProps<TData>
  extends Pick<
      StyledTableProps,
      'color' | 'size' | 'stickyHeader' | 'className' | 'containerClassName'
    >,
    Partial<TableOptions<TData>> {
  rowStyle?: 'classic' | 'cards';
  headerColor?: TableRowProps['color'];
  columns: ColumnDef<TData>[];
  data: TData[];
  empty?: React.ReactNode;
  hideHeaders?: boolean;
  hideHeadersIfEmpty?: boolean;
  enablePagination?: boolean;
  hideDefaultExpandColumn?: boolean;
  hideDefaultSelectionColumn?: boolean;
  disableExpandOnClick?: boolean;
  disableSelectOnClick?: boolean;
  disableMultiExpansion?: boolean;
  disableRowAnimation?: boolean;
  onRowClick?: (row: Row<TData>) => void;
  navigateTo?: (row: Row<TData>) => string | undefined;
}

const filterFn = <TValue>(value: TValue, filterValue: string): boolean => {
  if (value === null || value === undefined) return false;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint' ||
    typeof value === 'symbol'
  )
    return normalizeString(value.toString()).includes(normalizeString(filterValue));
  if (Array.isArray(value)) return value.some(value => filterFn(value, filterValue));
  if (typeof value === 'object')
    return Object.values(value).some(value => filterFn(value, filterValue));
  return false;
};

export const globalFilterFn = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
): boolean => {
  const value = row.getValue(columnId);
  return filterFn(value, filterValue);
};

export const getColumnCanGlobalFilter = () => true;
